import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useStockItemNotes, useCreateStockItemNote } from "@/api/dashboard";

/** Welle 2 (#15b): Artikel-Notizen als Popover je Reichweiten-Zeile.
 *  Rein informativ: Historie (Datum · Autor · Text) + Neuanlage; kein Status, kein Löschen.
 *  Die Liste lädt lazy — erst beim Öffnen (enabled über open-State). */
export function StockNotePopover({
  stockItemId,
  articleLabel,
}: {
  stockItemId: number;
  articleLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const { data, isLoading, isError, refetch } = useStockItemNotes(open ? stockItemId : null);
  const createNote = useCreateStockItemNote();
  const notes = data?.notes ?? [];

  function save() {
    const note = draft.trim();
    if (note.length === 0) return;
    createNote.mutate(
      { stock_item_id: stockItemId, note },
      {
        onSuccess: () => {
          setDraft("");
          toast.success("Notiz gespeichert");
        },
        onError: () => toast.error("Notiz konnte nicht gespeichert werden"),
      },
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="size-7 p-0"
          aria-label={`Notizen zu ${articleLabel}`}
          onClick={(e) => e.stopPropagation()}
        >
          <MessageSquare className="size-3.5 text-ink-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-3" align="end" onClick={(e) => e.stopPropagation()}>
        <div className="text-sm font-medium text-ink">Notizen · {articleLabel}</div>
        {isLoading ? (
          <div className="text-xs text-ink-2">Lädt…</div>
        ) : isError ? (
          <div role="alert" className="text-xs text-ink-2">Notizen konnten nicht geladen werden. <Button size="sm" variant="outline" onClick={() => void refetch()}>Erneut laden</Button></div>
        ) : notes.length === 0 ? (
          <div className="text-xs text-ink-2">Noch keine Notizen.</div>
        ) : (
          <ul className="max-h-56 space-y-2 overflow-y-auto">
            {notes.map((n) => (
              <li key={n.id} className="rounded border border-line bg-panel-2 px-2.5 py-1.5">
                <div className="text-[11px] text-ink-2">
                  {new Date(n.created_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}
                  {n.created_by ? ` · ${n.created_by}` : ""}
                </div>
                <div className="whitespace-pre-wrap text-xs text-ink">{n.note}</div>
              </li>
            ))}
          </ul>
        )}
        <div className="space-y-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Neue Notiz…"
            rows={2}
            maxLength={2000}
            className="text-xs"
          />
          <Button
            size="sm"
            className="w-full"
            disabled={draft.trim().length === 0 || createNote.isPending}
            onClick={save}
          >
            Notiz speichern
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
